<template>
  <div class="payday">
    <b-input-group>
      <b-form-input
          :id="`payment_${timesheetId}_${rowIndex}`"
          ref="input"
          v-model.number="payment.amount"
          :disabled="isInputDisabled"
          :name="`payment_${timesheetId}_${rowIndex}`"
          inputmode="decimal"
          min="0"
          pattern="[0-9]*"
          step="any"
          style="max-width: 100px; width: 100px; border: thin solid #e6007e;"
          type="number"
          @change="handleInputChange"
          @focus="handleInputFocus"
          @keydown="handleKeyDown"
      />

      <b-input-group-append style="margin-top: 0; min-height: 100%; height: 100%;">
        <b-button
            ref="worked"
            :disabled="disabled"
            :variant="workedSelected ? 'info' : 'outline-info'"
            class="btn worked"
            @click="setWorked"
            @keydown="handleWorkedKeyDown"
        >
          Worked
        </b-button>

        <b-button
            v-for="type in orderAbsences"
            :key="type"
            :ref="type"
            :class="'btn ' + type"
            :disabled="disabled"
            :variant="payment.absence_type === type ? 'info' : 'outline-info'"
            @click="() => setAbsence(type)"
            @keydown="(e) => handleAbsenceKeyDown(type, e)"
        >
          {{ absence_types[type] }}
        </b-button>
      </b-input-group-append>
    </b-input-group>
  </div>
</template>

<script>
export default {
  props: {
    disabled: Boolean,
    timesheetId: { required: true },
    rowIndex: { required: true },
    value: { type: Object, required: true }
  },
  data() {
    if (this.value.pay_date == null) {
      throw new Error('Cannot render pay day form for null date');
    }
    return {
      absence_types: { H: 'Holiday', S: 'Sick', M: 'Misc' },
      orderAbsences: ['H', 'S', 'M'],
      initial: {
        ...this.value,
        amount: parseFloat(this.value.amount) || 0,
        absence_type: this.value.absence_type || null,
        pay_date: this.value.pay_date
      },
      payment: {
        ...this.value,
        amount: parseFloat(this.value.amount) || 0,
        absence_type: this.value.absence_type || null,
        pay_date: this.value.pay_date
      },
      dirty: false
    };
  },
  computed: {
    isAbsenceSelected() {
      return this.payment.absence_type !== null;
    },
    isInputDisabled() {
      if (this.disabled) return true;
      return !this.dirty && this.isAbsenceSelected;
    },
    workedSelected() {
      return this.dirty && this.payment.absence_type === null && this.payment.amount > 0;
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.payment = { ...newVal };
      }
    }
  },
  mounted() {
    if (
        (this.payment.amount > 0 && this.payment.absence_type === null) ||
        this.payment.absence_type !== null
    ) {
      this.dirty = true;
    }
  },
  methods: {
    // ------- helpers -------
    amountIsEmpty() {
      const val = parseFloat(this.payment.amount);
      // Treat 0 or NaN/empty as "no amount entered"
      return !val || val <= 0;
    },
    focusAbsence(type) {
      const ref = this.$refs[type];
      const el = Array.isArray(ref) ? ref[0] : ref;
      el?.focus?.();
    },
    focusFirstAbsence() {
      this.focusAbsence(this.orderAbsences[0]);
    },
    focusPrevAbsence(currType) {
      const idx = this.orderAbsences.indexOf(currType);
      if (idx <= 0) {
        this.focusInput();
      } else {
        this.focusAbsence(this.orderAbsences[idx - 1]);
      }
    },
    focusNextAbsenceOrNextRow(currType) {
      const idx = this.orderAbsences.indexOf(currType);
      if (idx === -1 || idx === this.orderAbsences.length - 1) {
        // Past last (Misc) -> ask parent to focus next row's amount
        this.$emit('advanceFocus', {
          timesheetId: this.timesheetId,
          rowIndex: this.rowIndex,
          direction: 1
        });
      } else {
        this.focusAbsence(this.orderAbsences[idx + 1]);
      }
    },

    // ------- data update -------
    emitChange() {
      this.$emit('input', { ...this.payment });
    },
    setAbsence(type) {
      this.dirty = true;
      this.payment.absence_type = type;
      this.payment.amount = 0;
      this.emitChange();
      this.$emit('validate');
      // NOTE: no auto-advance here; Space/Enter selects only.
    },
    setWorked() {
      this.payment.absence_type = null;
      this.emitChange();
      this.$emit('validate');
      this.$nextTick(() => {
        this.$refs.input?.focus();
        this.$refs.input?.select();
      });
    },

    handleInputChange() {
      const val = parseFloat(this.payment.amount);
      if (!isNaN(val) && val >= 0) {
        this.payment.amount = val;
        this.dirty = true;
        this.payment.absence_type = null;
      } else {
        this.payment.amount = this.initial.amount;
      }
      this.emitChange();
      this.$emit('validate');
    },
    handleInputFocus(event) {
      event.target.select();
      this.$emit('validate');
    },

    // ------- key handling -------
    // Input: Right/Down/Enter/Tab -> forward; Left/Up/Shift+Tab -> backward
    // If no amount: forward goes to absence buttons (H → S → M), then parent (next row).
    // Blocks e/E/+/- for numeric field.
    handleKeyDown(event) {
      if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault();
        return;
      }
      let direction = null;
      switch (event.key) {
        case 'Tab': direction = event.shiftKey ? -1 : 1; break;
        case 'Enter':
        case 'ArrowRight':
        case 'ArrowDown': direction = 1; break;
        case 'ArrowLeft':
        case 'ArrowUp': direction = -1; break;
        default: return;
      }

      event.preventDefault();

      if (direction === 1) {
        if (this.amountIsEmpty()) {
          // Go into absence strip starting at Holiday
          this.focusFirstAbsence();
        } else {
          // Amount present -> next row
          this.$emit('advanceFocus', {
            timesheetId: this.timesheetId,
            rowIndex: this.rowIndex,
            direction: 1
          });
        }
      } else {
        // Backward -> previous row
        this.$emit('advanceFocus', {
          timesheetId: this.timesheetId,
          rowIndex: this.rowIndex,
          direction: -1
        });
      }
      this.$emit('validate');
    },

    // Worked button: backward -> input, forward -> first absence
    handleWorkedKeyDown(e) {
      let direction = null;
      switch (e.key) {
        case 'Tab': direction = e.shiftKey ? -1 : 1; break;
        case 'Enter':
        case 'ArrowRight':
        case 'ArrowDown': direction = 1; break;
        case 'ArrowLeft':
        case 'ArrowUp': direction = -1; break;
        default: return;
      }
      e.preventDefault();
      if (direction === -1) this.focusInput();
      else this.focusFirstAbsence();
    },

    // Absence buttons: Space/Enter selects; arrows/tab move between H,S,M; past M -> next row.
    handleAbsenceKeyDown(type, e) {
      // Space (modern browsers ' '), legacy 'Spacebar', or Enter -> select, stay
      if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
        e.preventDefault();
        this.setAbsence(type);
        return;
      }

      let direction = null;
      switch (e.key) {
        case 'Tab': direction = e.shiftKey ? -1 : 1; break;
        case 'ArrowRight':
        case 'ArrowDown': direction = 1; break;
        case 'ArrowLeft':
        case 'ArrowUp': direction = -1; break;
        default: return;
      }
      e.preventDefault();

      if (direction === -1) {
        this.focusPrevAbsence(type);
      } else {
        this.focusNextAbsenceOrNextRow(type);
      }
    },

    focusInput() {
      this.$emit('validate');
      this.$nextTick(() => {
        this.$refs.input?.focus();
        this.$refs.input?.select();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
div.payday {
  width: 100%;
  display: grid;
  grid-template-columns: auto;
  max-width: 350px;
}

.btn:active {
  color: white;
  &.H { background-color: yellow; }
  &.S { background-color: hotpink; }
  &.M { background-color: lightslategrey; }
}

.btn-outline-info {
  color: #e6007e;
  border-color: #e6007e;
  background-color: transparent;
}
.btn-outline-info:active { color: white !important; background-color: #e6007e !important; }
.btn-outline-info:hover { color: white; background-color: #f6108e; }

.btn-info {
  box-sizing: border-box;
  &.worked { background-color: lightgreen; }
  color: white;
  &.H { color: black; background-color: yellow; }
  &.S { background-color: hotpink; }
  &.M { background-color: lightslategrey; }
}
.btn-info:hover { color: white !important; background-color: #e6007e !important; }
</style>
